import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { ConfirmModal } from '../../settings/kit'
import {
  canVerifyCard,
  isActiveCard,
  isStatusDanger,
  MAX_COLLAPSE,
  OF_ADD_CARD,
  OF_CONTACT,
  OF_PAYMENTS,
  OF_VERIFICATION,
  verificationKey,
  withDefault,
  type PaymentCard,
} from './cardsData'
import { useCards } from './context'
import { Alert, BrandIcon, DotsMenu, OfLink, OfOnly } from './parts'
import { Loader } from './Transactions'

/** /my/payments: the age-check alerts, "add a card" alerts and the card list. */
export function CardsTab() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { me, cards, mobile, canWrite } = useCards()
  const [expanded, setExpanded] = useState(false)
  const list = cards ?? []
  const noCards = cards !== null && !list.length
  const noCanPay = list.length > 0 && !list.some((c) => c.canPay)
  const shown = list.length > MAX_COLLAPSE && !expanded ? list.slice(0, MAX_COLLAPSE) : list
  const ivApproved = me.ivStatus === 'approved'

  return (
    <div className="ofc-cards">
      {mobile && (
        <div className="ofc-subhead">
          <h2>{t('tabs.cards')}</h2>
          {me.canAddCard && (
            <button type="button" className="ofc-icon-btn is-gray" aria-label={t('addCard')} title={`${t('addCard')} — ${t('only.addCard')}`} disabled>
              <FeatherIcon name="credit-card" size={20} />
            </button>
          )}
        </div>
      )}
      {me.ageVerificationRequired && me.ageVerificationStatus === 'pending' ? (
        <Alert loading>{t('age.pending')}</Alert>
      ) : me.ageVerificationStatus === 'failed' && !ivApproved && me.needIVApprove ? (
        <Alert variant="danger">
          {t('age.failed')}
          <div className="ofc-alert-btns">
            <a className="oset-btn oset-btn--primary oset-btn--sm" href={OF_VERIFICATION} target="_blank" rel="noopener noreferrer">
              {t('verify')}
            </a>
          </div>
        </Alert>
      ) : me.ageVerificationRequired && !me.isAgeVerified ? (
        <Alert variant="danger">
          {t('age.please')} <OfLink href={OF_PAYMENTS}>{t('age.verify')}</OfLink>
        </Alert>
      ) : null}
      {cards === null ? (
        <Loader />
      ) : (
        <>
          {noCards && (
            <Alert variant="danger">
              {me.canAddCard ? (
                <Trans t={t} i18nKey="alerts.noCard" components={{ a: <OfLink href={OF_ADD_CARD} /> }} />
              ) : (
                <Trans t={t} i18nKey="alerts.cantAddCard" components={{ a: <OfLink href={OF_CONTACT} /> }} />
              )}
            </Alert>
          )}
          {noCanPay && (
            <Alert variant="danger">
              {me.canAddCard ? (
                <Trans t={t} i18nKey="alerts.noCanPay" components={{ a: <OfLink href={OF_ADD_CARD} /> }} />
              ) : (
                <Trans t={t} i18nKey="alerts.cantAddCard" components={{ a: <OfLink href={OF_CONTACT} /> }} />
              )}
            </Alert>
          )}
          {(noCards || noCanPay) && me.canAddCard && <OfOnly text={t('only.addCard')} href={OF_ADD_CARD} />}
        </>
      )}
      {list.length > 0 && (
        <>
          <div className="ofc-card-rows">
            {shown.map((c) => (
              <CardRow key={c.id} card={c} canWrite={canWrite} />
            ))}
          </div>
          {list.length > MAX_COLLAPSE && (
            <button type="button" className="ofc-collapse" onClick={() => setExpanded((e) => !e)}>
              {expanded ? t('viewLess') : t('viewAll')}
              <FeatherIcon name={expanded ? 'chevron-up' : 'chevron-down'} size={18} />
            </button>
          )}
        </>
      )}
    </div>
  )
}

/** PaymentsCard: brand and number, status, expiry, "…" menu, Verify / Make default. */
function CardRow({ card, canWrite }: { card: PaymentCard; canWrite: boolean }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { of, referer, me, setCards, reloadCards, syncPaypal, mergeMe, say } = useCards()
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const active = isActiveCard(card)
  const error = card.lastError || (!card.canPay ? t('card.unableToUse') : '')
  const disabled = busy || !canWrite
  const fail = (e: unknown) => say(e instanceof Error && e.message ? e.message : t('error'))

  /** setDefaultCard: PUT payments/cards/<id>/default, then the list again. */
  const makeDefault = async () => {
    setBusy(true)
    try {
      const r = await of.send<{ success?: boolean }>('PUT', `payments/cards/${card.id}/default`, referer)
      if (r?.success) {
        setCards((all) => withDefault(all, card.id))
        await reloadCards()
        void syncPaypal()
      }
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  /** deleteCard: DELETE payments/cards/<id>; OnlyFans answers with the user's new card flags. */
  const remove = async () => {
    setBusy(true)
    try {
      const r = await of.send<{ success?: boolean; isPaymentCardConnected?: boolean; canAddCard?: boolean }>('DELETE', `payments/cards/${card.id}`, referer)
      if (r?.success) {
        setCards((all) => all.filter((c) => c.id !== card.id))
        if (card.isDefault) await reloadCards()
        const patch: Record<string, boolean> = {}
        if (typeof r.isPaymentCardConnected === 'boolean') patch.isPaymentCardConnected = r.isPaymentCardConnected
        if (typeof r.canAddCard === 'boolean') patch.canAddCard = r.canAddCard
        mergeMe(patch)
        void syncPaypal()
      }
    } finally {
      setBusy(false)
    }
  }

  const classes = [
    'ofc-card',
    !card.canPay && 'is-cant-pay',
    card.isExpired && 'is-expired',
    card.isDefault && 'is-default',
    me.requireCardVerification && `is-${card.verificationStatus}`,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes}>
      <div className="ofc-card-row">
        <div className="ofc-card-perf">
          {card.brand && (
            <span className="ofc-card-brand">
              <BrandIcon brand={card.brand} />
              <span className="ofc-card-name">{card.brand}</span>
            </span>
          )}
          <span className="ofc-card-number">···· ···· ···· {card.last4}</span>
        </div>
      </div>
      <div className="ofc-card-row">
        {error ? (
          <span className="ofc-card-error">{error}</span>
        ) : (
          <span className={`ofc-card-status ${isStatusDanger(card, me) ? 'is-danger' : ''}`}>
            {card.verificationStatus === 'verified' && <FeatherIcon name="check" size={16} />}
            {t(verificationKey(card, me))}
          </span>
        )}
        {card.expireDate &&
          (card.isExpired ? (
            <strong className="ofc-card-exp">{t('card.expiration', { date: card.expireDate })}</strong>
          ) : (
            <span className="ofc-card-exp">{t('card.expiration', { date: card.expireDate })}</span>
          ))}
        <DotsMenu
          label={t('card.more')}
          items={[
            ...(active
              ? [
                  {
                    label: t('card.editAddress'),
                    disabled: true,
                    note: <OfOnly text={t('only.secure')} href={OF_PAYMENTS} />,
                  },
                ]
              : []),
            { label: t('card.delete'), danger: true, divider: active, disabled, onClick: () => setConfirm(true) },
          ]}
        />
      </div>
      <div className="ofc-card-btns">
        {canVerifyCard(card, me) && (
          <button type="button" className="ofc-pill-btn" disabled>
            {t('verify')}
          </button>
        )}
        {active && (
          <button type="button" className="ofc-pill-btn" disabled={disabled || card.isDefault} onClick={() => void makeDefault()}>
            {busy || card.isDefault ? t('card.default') : t('card.makeDefault')}
          </button>
        )}
      </div>
      {canVerifyCard(card, me) && <OfOnly text={t('only.secure')} href={OF_PAYMENTS} />}
      {confirm && <ConfirmModal text={t('card.deleteConfirm')} confirm={t('card.deleteYes')} onConfirm={remove} onClose={() => setConfirm(false)} />}
    </div>
  )
}
